import React, { useEffect, useRef, useState } from "react";
import { Bell, X, MessageSquare, Calendar, Users, Trophy } from "lucide-react";
import api, { API_BASE } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { useNavigate } from "react-router-dom";

const iconFor = (type) => {
  if (type === "new_message") return MessageSquare;
  if (type === "training_created") return Calendar;
  if (type === "player_added") return Users;
  if (type === "match_created") return Trophy;
  if (type === "rsvp_update") return Calendar;
  return Bell;
};

export default function NotificationsBell() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const wsRef = useRef(null);
  const popRef = useRef(null);

  const unread = items.filter((n) => !n.read).length;

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    const wsUrl = API_BASE.replace(/^http/, "ws") + `/ws/chat?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "notification") {
            setItems((prev) => [m.data, ...prev].slice(0, 50));
          }
        } catch (_) {}
      };
    } catch (_) {}
    return () => {
      try { ws?.close(); } catch (_) {}
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (open && popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const click = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (_) {}
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) nav(n.link);
    setOpen(false);
  };

  const ago = (iso) => {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return lang === "bg" ? "сега" : "now";
    if (m < 60) return `${m}${lang === "bg" ? "м" : "m"}`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}${lang === "bg" ? "ч" : "h"}`;
    const d = Math.floor(h / 24);
    return `${d}${lang === "bg" ? "д" : "d"}`;
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center border border-white/10 hover:border-pitch transition-colors"
        data-testid="notif-bell"
        aria-label="notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-pitch text-white text-[10px] font-bold flex items-center justify-center px-1 badge-pulse rounded-full">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-w-[92vw] glass-elevated shadow-2xl z-50 animate-fade-up" data-testid="notif-panel">
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <div className="label-mono">// {t("notifications")}</div>
            <div className="flex items-center gap-3">
              <button onClick={markAll} className="label-mono hover:text-pitch transition-colors" data-testid="notif-read-all">
                {t("markAllRead")}
              </button>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-white/40 hover:text-white" /></button>
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto scroll-thin">
            {items.length === 0 ? (
              <div className="p-8 text-center text-white/30 text-xs font-mono tracking-widest">
                {t("noNotifications")}
              </div>
            ) : items.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <button
                  key={n.id}
                  onClick={() => click(n)}
                  className={`notif-item ${!n.read ? "unread" : ""} w-full text-left flex gap-3`}
                  data-testid={`notif-${n.id}`}
                >
                  <div className="w-9 h-9 flex-shrink-0 bg-pitch/10 text-pitch flex items-center justify-center rounded-full">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-oswald uppercase tracking-tight text-sm truncate">{n.title}</div>
                      <div className="text-[10px] font-mono text-white/30 whitespace-nowrap">{ago(n.created_at)}</div>
                    </div>
                    {n.body && <div className="text-xs text-white/55 truncate mt-0.5">{n.body}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
